/**
 * The configuration a run used, and a way to save it as a reusable profile
 * after the fact.
 *
 * This exists because the moment to save a mapping is easy to miss, and
 * missing it used to mean re-doing the mapping by hand next week. The run
 * records what it read, so the profile can always be recovered from it —
 * saving is no longer a one-shot decision made before you have seen whether
 * the run was any good.
 *
 * SPEC-0001 step 6.
 */
import { useState } from 'react';
import { fieldsFor, type RunMapping, type SourceType } from '@picklii/shared';
import { api } from '../lib/api';
import { Icon } from './Icon';

const POLICY_LABEL: Record<RunMapping['duplicatePolicy'], string> = {
	reject: 'Stop and show the duplicates',
	sum: 'Add their hours together',
	latest: 'Keep the most recent row',
};

function MappingColumns({ source, mapping }: { source: SourceType; mapping: Record<string, string> }) {
	return (
		<table className="table table-sm">
			<thead>
				<tr>
					<th>Picklii field</th>
					<th>Your column</th>
				</tr>
			</thead>
			<tbody>
				{fieldsFor(source).map((field) => {
					const column = mapping[field.key];
					return (
						<tr key={field.key}>
							<td className="font-medium">{field.label}</td>
							<td>
								{column ?? <span className="text-base-content/70 italic">not mapped</span>}
							</td>
						</tr>
					);
				})}
			</tbody>
		</table>
	);
}

export function RunMappingPanel({ mapping }: { mapping: RunMapping }) {
	// One name per side: they save as two independent profiles.
	const [names, setNames] = useState({
		sold: mapping.usedProfiles?.sold ?? '',
		budget: mapping.usedProfiles?.budget ?? '',
	});
	const [savedSides, setSavedSides] = useState<Record<SourceType, boolean>>({
		sold: mapping.usedProfiles?.sold != null,
		budget: mapping.usedProfiles?.budget != null,
	});
	const [busy, setBusy] = useState<SourceType | null>(null);
	const [error, setError] = useState<string | null>(null);

	async function save(side: SourceType) {
		setBusy(side);
		setError(null);
		try {
			await api.saveProfile({
				name: names[side].trim(),
				sourceType: side,
				columns: side === 'sold' ? mapping.soldMapping : mapping.budgetMapping,
				aliases: mapping.aliases,
			});
			setSavedSides((current) => ({ ...current, [side]: true }));
		} catch (cause) {
			setError(cause instanceof Error ? cause.message : 'Could not save the mapping.');
		} finally {
			setBusy(null);
		}
	}

	return (
		<section className="card bg-base-200 border-base-300 col-span-12 border shadow-xs">
			<div className="card-body gap-4">
				<div>
					<h2 className="card-title text-base">Column mapping used</h2>
					<p className="text-base-content/70 text-sm">
						Exactly which of your columns this run read, and how it handled repeats. Save
						either side as a reusable profile for that source.
					</p>
				</div>

				{error !== null && (
					<div className="alert alert-error alert-soft" role="alert">
						<span>{error}</span>
					</div>
				)}

				<div className="grid gap-6 lg:grid-cols-2">
					{(['sold', 'budget'] as const).map((side) => (
						<div key={side}>
							<h3 className="mb-2 font-medium">
								{side === 'sold' ? 'Sold hours' : 'PSA budget'}
							</h3>
							<MappingColumns
								source={side}
								mapping={side === 'sold' ? mapping.soldMapping : mapping.budgetMapping}
							/>
							<div className="mt-2">
								{savedSides[side] ? (
									<span className="badge badge-success badge-sm">
										Used “{names[side]}”
									</span>
								) : (
									<div className="join">
										<input
											className="input input-bordered input-sm join-item"
											placeholder="Name this mapping"
											value={names[side]}
											onChange={(e) => setNames({ ...names, [side]: e.target.value })}
											aria-label={`Profile name for the ${side} mapping`}
										/>
										<button
											className="btn btn-sm btn-primary join-item"
											disabled={busy !== null || names[side].trim() === ''}
											onClick={() => void save(side)}>
											{busy === side ? (
												<span className="loading loading-spinner loading-xs" />
											) : (
												'Save for reuse'
											)}
										</button>
									</div>
								)}
							</div>
						</div>
					))}
				</div>

				<div className="flex flex-wrap gap-6 text-sm">
					<div>
						<span className="text-base-content/70">Repeated project + role rows: </span>
						{POLICY_LABEL[mapping.duplicatePolicy]}
					</div>
					<div>
						<span className="text-base-content/70">Role aliases: </span>
						{mapping.aliases.length === 0
							? 'none'
							: mapping.aliases.map((a) => `${a.from} → ${a.to}`).join(', ')}
					</div>
					{/*
					 * Only shown once there is something to say: a single-file run's
					 * mapping panel already names it implicitly. Once a change order
					 * arrives as a second sold-hours export (issue #30), this is the
					 * record of exactly which files fed the reconciliation.
					 */}
					{mapping.soldFileNames !== undefined && mapping.soldFileNames.length > 1 && (
						<div>
							<span className="text-base-content/70">Sold-hours files: </span>
							{mapping.soldFileNames.join(', ')}
						</div>
					)}
				</div>

				<a href="/runs/new" className="btn btn-sm btn-outline self-end">
					<Icon name="upload" />
					Re-run with adjustments
				</a>
			</div>
		</section>
	);
}
